/** DLRP1 share links — the QR/phone handoff. docs/SHARE_LINK_SPEC.md is
 * the contract; the phone implements the same bytes. Control points only,
 * never solved geometry: the receiver re-solves snapped legs on the SAME
 * legal network, which is what keeps a whole plan inside a scannable QR. */

import type { LonLat, Plan, PlanLeg, Waypoint } from "./types";

export const SHARE_PATH = "/p";

const POLYLINE_PRECISION = 1e5;

/** One dimension's delta, packed into the algorithm's base64-ish alphabet:
 * zigzag the signed delta into an unsigned value (so negatives don't need
 * a sign bit of their own), then emit 5 bits at a time, least-significant
 * chunk first, with the continuation bit (0x20) set on every chunk but the
 * last. +63 lands every byte in a printable ASCII range. */
function encodeDelta(delta: number): string {
  let value = delta << 1;
  if (delta < 0) value = ~value;
  let out = "";
  while (value >= 0x20) {
    out += String.fromCharCode((0x20 | (value & 0x1f)) + 63);
    value >>= 5;
  }
  out += String.fromCharCode(value + 63);
  return out;
}

/** Google Encoded Polyline Algorithm, 1e-5, lat,lon order — the phone's
 * TripPDF.polyline/decodePolyline are the reference implementation. Lat
 * and lon each delta-code against their OWN previous rounded integer
 * (never against each other), which is what lets the decoder replay the
 * same running sums in lockstep. */
export function encodePolyline(latLon: [number, number][]): string {
  let out = "";
  let prevLat = 0;
  let prevLon = 0;
  for (const [lat, lon] of latLon) {
    const latI = Math.round(lat * POLYLINE_PRECISION);
    const lonI = Math.round(lon * POLYLINE_PRECISION);
    out += encodeDelta(latI - prevLat);
    out += encodeDelta(lonI - prevLon);
    prevLat = latI;
    prevLon = lonI;
  }
  return out;
}

export function decodePolyline(s: string): [number, number][] {
  const points: [number, number][] = [];
  let index = 0;
  let lat = 0;
  let lon = 0;
  while (index < s.length) {
    let result = 0;
    let shift = 0;
    let b: number;
    do {
      b = s.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    lat += result & 1 ? ~(result >> 1) : result >> 1;

    result = 0;
    shift = 0;
    do {
      b = s.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    lon += result & 1 ? ~(result >> 1) : result >> 1;

    points.push([lat / POLYLINE_PRECISION, lon / POLYLINE_PRECISION]);
  }
  return points;
}

/** 5-decimal rounding for waypoint coordinates — matches the polyline's
 * own precision, so nothing in the payload carries more resolution than
 * the route line itself does. */
function round5(n: number): number {
  return Math.round(n * 1e5) / 1e5;
}

// CompressionStream/DecompressionStream("deflate-raw") is raw DEFLATE, no
// zlib header — and that's the point: iOS's Compression framework
// COMPRESSION_ZLIB is ALSO raw DEFLATE despite the name (the classic
// pitfall this spec calls out), so the two interoperate with neither side
// writing the 2-byte zlib wrapper. Piping through a Response is the
// shortest correct way to drain a ReadableStream to bytes; both are global
// in browsers and Node 18+.
//
// A malformed inflate input (see decodeShareFragment's own try/catch)
// errors BOTH sides of the pipe — the readable side is what we actually
// await/act on below, but the writable side's write()/close() promises
// reject too, and left unobserved that's an unhandled-rejection warning
// even though the caller correctly sees and handles the failure via the
// read side. The no-op .catch marks it observed without changing what
// either function resolves or rejects with.
async function pipeThrough(
  bytes: Uint8Array,
  stream: CompressionStream | DecompressionStream,
): Promise<Uint8Array> {
  const writer = stream.writable.getWriter();
  // TS's DOM lib wants ArrayBufferView<ArrayBuffer> specifically (not the
  // wider ArrayBufferLike a bare Uint8Array carries, which also admits
  // SharedArrayBuffer) — every Uint8Array here is our own regular,
  // non-shared allocation (TextEncoder output or `new Uint8Array(n)`), so
  // this is a real-shape assertion, not a suppression.
  writer.write(bytes as BufferSource).then(() => writer.close()).catch(() => {});
  return new Uint8Array(await new Response(stream.readable).arrayBuffer());
}

async function deflateRaw(bytes: Uint8Array): Promise<Uint8Array> {
  return pipeThrough(bytes, new CompressionStream("deflate-raw"));
}

async function inflateRaw(bytes: Uint8Array): Promise<Uint8Array> {
  return pipeThrough(bytes, new DecompressionStream("deflate-raw"));
}

/** btoa/atob work on binary strings, not bytes — the loop is the standard
 * bridge. Payloads here are at most a few KB (see the spec's size budget),
 * nowhere near where a per-byte loop would matter. */
function toBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** Null on anything that isn't valid base64url (or valid base64 once
 * padding is restored) — atob throws on garbage, which the caller treats
 * as "not a DLRP1 payload" rather than a crash. */
function fromBase64Url(s: string): Uint8Array | null {
  try {
    const base64 = s.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes;
  } catch {
    return null;
  }
}

/** Plan -> full share URL (https://app.dustlark.app/p#<payload>).
 * Async because compression streams are. */
export async function encodeShareURL(plan: Plan): Promise<string> {
  const polyline = encodePolyline(plan.points.map(([lon, lat]): [number, number] => [lat, lon]));
  const modes = plan.legs.map((leg) => (leg.mode === "snapped" ? "s" : "t")).join("");
  const waypoints = plan.waypoints.map(
    (w): [number, number, string] => [round5(w.at[1]), round5(w.at[0]), w.name],
  );
  const payload: Record<string, unknown> = {
    v: 1,
    n: plan.name,
    r: plan.region,
    p: polyline,
    m: modes,
    w: waypoints,
  };
  // Optional and sparse in practice (v1 draws straight/snapped legs only,
  // no day planning yet) — omitted entirely rather than shipped as `[]`.
  if (plan.dayBreaks && plan.dayBreaks.length > 0) payload.d = plan.dayBreaks;

  const json = JSON.stringify(payload);
  const compressed = await deflateRaw(new TextEncoder().encode(json));
  return `https://app.dustlark.app${SHARE_PATH}#${toBase64Url(compressed)}`;
}

/** Fragment (with or without leading '#') -> Plan, or null when it isn't
 * a DLRP1 payload / fails to decode. `now` stamps createdAt/updatedAt.
 * A payload with v > 1 returns null (the caller says "needs a newer
 * app") — never a half-guessed plan. */
export async function decodeShareFragment(
  fragment: string,
  now: string,
): Promise<Plan | null> {
  const raw = fragment.startsWith("#") ? fragment.slice(1) : fragment;
  if (!raw) return null;

  const compressed = fromBase64Url(raw);
  if (!compressed) return null;

  let json: unknown;
  try {
    const jsonText = new TextDecoder().decode(await inflateRaw(compressed));
    json = JSON.parse(jsonText);
  } catch {
    return null; // malformed deflate stream, or not valid UTF-8/JSON once inflated
  }
  if (!json || typeof json !== "object") return null;
  const payload = json as Record<string, unknown>;

  // v>1 means "needs a newer app" — never guess at an unknown schema.
  if (payload.v !== 1) return null;
  if (typeof payload.p !== "string" || typeof payload.m !== "string" || typeof payload.r !== "string") {
    return null;
  }

  const points: LonLat[] = decodePolyline(payload.p).map(([lat, lon]) => [lon, lat]);
  if (points.length < 2) return null;

  const modeChars = payload.m.split("");
  if (modeChars.length !== points.length - 1) return null; // corrupt payload — never pad/truncate to fit
  const legs: PlanLeg[] = modeChars.map((c): PlanLeg => ({
    mode: c === "s" ? "snapped" : "straight",
    via: [], // control points only — the receiver re-solves `s` legs itself
  }));

  const waypoints: Waypoint[] = Array.isArray(payload.w)
    ? payload.w
        .filter(
          (w): w is [number, number, string] =>
            Array.isArray(w) && w.length >= 3 && typeof w[0] === "number" && typeof w[1] === "number",
        )
        .map((w) => ({ at: [w[1], w[0]] as LonLat, name: String(w[2]) }))
    : [];

  const plan: Plan = {
    name: typeof payload.n === "string" ? payload.n : "",
    region: payload.r,
    points,
    legs,
    waypoints,
    createdAt: now,
    updatedAt: now,
  };
  if (Array.isArray(payload.d) && payload.d.length > 0) {
    const dayBreaks = payload.d.filter((x): x is number => typeof x === "number");
    if (dayBreaks.length > 0) plan.dayBreaks = dayBreaks;
  }
  return plan;
}
